import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { DroneManufacturer } from '../../core/data-models/drone-manufacturer.model';
import { DroneStatus } from '../../core/data-models/drone-status.model';
import { Drone } from '../../core/data-models/drone.model';
import { ZohoSyncStatus } from '../../core/data-models/zoho-sync-status.model';
import { DroneManufacturerService } from '../../core/data-services/drone-manufacturer.service';
import { DroneService } from '../../core/data-services/drone.service';

@Component({
  selector: 'app-drones',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './drones.component.html',
  styleUrl: './drones.component.scss',
})
export class DronesComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly droneService = inject(DroneService);
  private readonly droneManufacturerService = inject(DroneManufacturerService);

  drones: Drone[] = [];
  allDrones: Drone[] = [];
  loading = true;
  errorMessage = '';

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalDrones = 0;
  totalPages = 0;

  // Filters
  searchQuery = '';
  filterStatus: 'all' | 'available' | 'assigned' | 'maintenance' | 'retired' = 'all';
  filterManufacturer: string = 'all';
  sortBy: 'createdAt' | 'name' | 'serialNumber' = 'createdAt';
  sortOrder: 'asc' | 'desc' = 'desc';

  // Available manufacturers for filter dropdown
  manufacturers: DroneManufacturer[] = [];

  ngOnInit(): void {
    this.loadManufacturers();
    this.loadDrones();
  }

  async loadManufacturers(): Promise<void> {
    try {
      this.manufacturers = await firstValueFrom(this.droneManufacturerService.getManufacturers());
    } catch (error: any) {
      console.error('Failed to load manufacturers:', error);
    }
  }

  async loadDrones(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      // All filters are applied together (AND logic) by the hub
      this.allDrones = await firstValueFrom(this.droneService.getAllDrones({
        status: this.filterStatus === 'all' ? undefined : this.filterStatus,
        manufacturerId: this.filterManufacturer === 'all' ? undefined : this.filterManufacturer,
        search: this.searchQuery,
      }));

      // Apply client-side sorting and pagination
      this.applySortingAndPagination();
    } catch (error: any) {
      console.error('Load drones error:', error);
      this.errorMessage = 'Failed to load drones';
    } finally {
      this.loading = false;
    }
  }

  private applySortingAndPagination(): void {
    let filteredDrones = [...this.allDrones];

    // Apply sorting
    filteredDrones.sort((a, b) => {
      let comparison = 0;
      switch (this.sortBy) {
        case 'name':
          comparison = (a.name ?? '').localeCompare(b.name ?? '');
          break;
        case 'serialNumber':
          comparison = (a.serialNumber ?? '').localeCompare(b.serialNumber ?? '');
          break;
        case 'createdAt':
        default:
          comparison = (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0);
          break;
      }
      return this.sortOrder === 'desc' ? -comparison : comparison;
    });

    this.totalDrones = filteredDrones.length;
    this.totalPages = Math.ceil(this.totalDrones / this.pageSize);

    // Apply pagination
    const startIndex = (this.currentPage - 1) * this.pageSize;
    this.drones = filteredDrones.slice(startIndex, startIndex + this.pageSize);
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadDrones();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadDrones();
  }
  
  showClearFilters(): boolean {
    return !!(this.searchQuery || this.filterStatus !== 'all' || this.filterManufacturer !== 'all' || this.sortBy !== 'createdAt' || this.sortOrder !== 'desc');
  }

  resetFilters(): void {
    this.searchQuery = '';
    this.filterStatus = 'all';
    this.filterManufacturer = 'all';
    this.sortBy = 'createdAt';
    this.sortOrder = 'desc';
    this.onFilterChange();
  }

  onSortChange(): void {
    this.applySortingAndPagination();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.applySortingAndPagination();
    }
  }

  viewDrone(droneId: string): void {
    this.router.navigate(['/drones', droneId, 'details']);
  }

  viewDroneDetails(droneId: string): void {
    this.router.navigate(['/drones', droneId, 'details']);
  }

  editDrone(droneId: string): void {
    this.router.navigate(['/drones', droneId, 'edit']);
  }

  createDrone(): void {
    this.router.navigate(['/drones/create']);
  }

  formatDate(value: Date | string | null | undefined): string {
    if (!value) return '—';
    const date = new Date(value);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getStatusClass(status: DroneStatus | null | undefined): string {
    switch (status) {
      case 'available': return 'bg-success';
      case 'assigned': return 'bg-primary';
      case 'leased': return 'bg-primary';
      case 'maintenance': return 'bg-warning';
      case 'retired': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  getStatusLabel(status: DroneStatus | null | undefined): string {
    switch (status) {
      case 'available': return 'Available';
      case 'assigned': return 'Assigned';
      case 'leased': return 'Leased';
      case 'maintenance': return 'Maintenance';
      case 'retired': return 'Retired';
      default: return status ?? '—';
    }
  }

  getZohoSyncStatusClass(syncStatus: ZohoSyncStatus | null | undefined): string {
    switch (syncStatus) {
      case 'synced': return 'text-success';
      case 'pending': return 'text-warning';
      case 'error': return 'text-danger';
      default: return 'text-muted';
    }
  }

  getZohoSyncStatusIcon(syncStatus: ZohoSyncStatus | null | undefined): string {
    switch (syncStatus) {
      case 'synced': return 'bi-cloud-check-fill';
      case 'pending': return 'bi-cloud-arrow-up';
      case 'error': return 'bi-cloud-slash-fill';
      default: return 'bi-cloud';
    }
  }

  getZohoSyncStatusTooltip(drone: Drone): string {
    switch (drone.zohoFsmSyncStatus) {
      case 'synced':
        return drone.zohoFsmSyncedAt
          ? `Synced to Zoho FSM on ${this.formatDate(drone.zohoFsmSyncedAt)}`
          : 'Synced to Zoho FSM';
      case 'pending': return 'Pending sync to Zoho FSM';
      case 'error': return drone.zohoFsmSyncError || 'FSM sync error';
      default: return 'Not synced to Zoho FSM';
    }
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  // Expose Math for template
  readonly Math = Math;
}
